import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import useMutationAuthMagicLink from '@/integrations/services/auth-magic-link/auth-magic-link.hook';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useLoginContext } from '@/libraries/contexts/login-context';
import styles from './login-curtain-verification-step.module.scss';

export interface LoginCurtainVerificationStepProps {
  /** The text to show on the button */
  buttonText?: string;
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: string;
  /** Explicit redirect target for the magic link */
  redirectTo?: string;
  /** The text to show as a description */
  text?: string;
}

const LoginCurtainVerificationStep = ({
  className,
  headline,
  buttonText,
  redirectTo,
  text,
}: LoginCurtainVerificationStepProps) => {
  const { t } = useI18n();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { email, retryTime } = useLoginContext();
  const [remainingTime, setRemainingTime] = useState(retryTime);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { isLoading, isSuccess, mutate } = useMutationAuthMagicLink();

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (remainingTime <= 0) {
      return;
    }

    timerRef.current = setTimeout(() => {
      setRemainingTime(value => value - 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [remainingTime]);

  useEffect(() => {
    if (!isSuccess) return;

    setRemainingTime(retryTime);
  }, [isSuccess, retryTime]);

  const resend = useMemo(() => {
    const secondsShort = t('items.modal.loginModal.secondsShort').replace(
      '{{secondsShort}}',
      `${remainingTime}`,
    );

    if (remainingTime > 0 && secondsShort) {
      return `${buttonText} (${secondsShort})`;
    }

    return buttonText;
  }, [buttonText, remainingTime, t]);

  const handleResend = () => {
    mutate({
      email,
      redirectTo:
        redirectTo ||
        (searchParams?.toString()
          ? `${pathname}?${searchParams.toString()}`
          : `${pathname}`),
    });
  };

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.header}>
        <Typography
          className={styles.headline}
          tagStyle="headingMedium"
          text={headline}
        />

        {text && (
          <RichText
            className={styles.text}
            text={text.replace('{{email}}', email ?? '')}
          />
        )}
      </div>

      <form className={styles.main}>
        <Button
          disabled={isLoading || remainingTime > 0}
          onClick={handleResend}
          text={resend}
          variant="tertiary"
        />
      </form>
    </div>
  );
};

LoginCurtainVerificationStep.displayName = 'LoginCurtainVerificationStep';

export default LoginCurtainVerificationStep;
